import React from 'react';

import get from 'lodash.get';
import dayjs from 'dayjs';
import { I18n, Translate } from 'react-redux-i18n';

import { isArrayWithItems } from '../../../helpers/arrayHelper';
import { getNewsletterOptInLabel } from '../../../utils/string/nullableBooleanLabel';

import { ViewTable } from '../../../components/layouts/viewTable';
import { FormBuilderSectionRow } from '../../../components/layouts/formBuilderSectionRow';
import { DonationAddressSectionRow } from './components/donationAddressSectionRow';
import { ViewSectionRow } from '../../../components/layouts/viewSectionRow';
import { DonationSectionBooleanRow } from './components/donationSectionBooleanRow';

import config from '../../../constants';

const UTM_FLAG_KEYS = [
  'utm-source',
  'utm-medium',
  'utm-campaign',
  'utm-content',
  'utm-term'
];

// The UTM flags that have a value, in display order.
const getUtmFlags = (flags) => {
  if (!Array.isArray(flags)) return [];

  return UTM_FLAG_KEYS.map((key) =>
    flags.find((flag) => flag.key === key)
  ).filter((flag) => flag && flag.value);
};

const getFundraiserUpdatesLabel = (donorCommunication) => {
  if (!donorCommunication) return I18n.t('common.not-available');

  const { fundraiserUpdatesOptIn, fundraiserUpdatesOptOutAt } = donorCommunication;

  if (fundraiserUpdatesOptIn === true && !fundraiserUpdatesOptOutAt) {
    return I18n.t('common.yes');
  }
  if (fundraiserUpdatesOptIn === true && fundraiserUpdatesOptOutAt) {
    const formattedDate = dayjs(fundraiserUpdatesOptOutAt)
      .format(config.INVERSION_FULL_DATE_TIME_PERIODS_FORMAT);
    return I18n.t(
      'donation.donation-view.donation-details.fundraiser-updates-initially-given-opted-out',
      { date: formattedDate }
    );
  }
  if (fundraiserUpdatesOptIn === false) {
    return I18n.t('common.no');
  }
  return I18n.t('common.not-available');
};

export const DonationAdditionalInformation = (props) => {
  const { fields, donor, displayName } = props.donation;
  const { isShowEmptyFields, donorCommunication, flags } = props;
  
  const utmFlags = getUtmFlags(flags);

  const phoneNumber = get(donor, 'phoneNumber');
  const address = get(donor, 'address');
  const anonymous = get(donor, 'anonymous');

  return (
    <React.Fragment>
      <div className="donation-additional-information">
        <ViewTable
          title={
            <Translate value="donation.donation-view.donation-details.additional-information" />
          }
        >
          <ViewSectionRow
            data={phoneNumber}
            title="donation.donation-view.donation-details.phone-number"
            isShowEmptyFields={isShowEmptyFields}
          />
          <DonationAddressSectionRow
            title="donation.donation-view.donation-details.address"
            data={address}
            isShowEmptyFields={isShowEmptyFields}
          />
          <div className="view-details-content-row">
            <h4>{I18n.t('donation.donation-view.donation-details.donor-communication-consent')}</h4>
            <span>
              <div><strong>{I18n.t('donation.donation-view.donation-details.charity-newsletter-opt-in')}</strong>: {donorCommunication ? getNewsletterOptInLabel(donorCommunication.newsletterOptIn) : I18n.t('common.not-available')}</div>
              <div><strong>{I18n.t('donation.donation-view.donation-details.fundraiser-updates-opt-in')}</strong>: {getFundraiserUpdatesLabel(donorCommunication)}</div>
            </span>
          </div>
          {isArrayWithItems(fields) &&
            React.Children.toArray(
              fields.map((field) => (
                <FormBuilderSectionRow
                  isShowEmptyFields={isShowEmptyFields}
                  field={field}
                />
              ))
            )}
          <DonationSectionBooleanRow
            title="donation.donation-view.donation-details.anonymous-donation"
            data={anonymous}
          />
          <ViewSectionRow
            title="donation.donation-view.donation-details.display-name"
            data={displayName}
            isShowEmptyFields={isShowEmptyFields}
          />
          {utmFlags.length > 0 && (
            <div className="view-details-content-row">
              <h4>{I18n.t('donation.donation-view.donation-details.utm-information')}</h4>
              <span>
                {utmFlags.map((flag) => (
                  <div key={flag.key}>
                    <strong>
                      {I18n.t(
                        `donation.donation-view.donation-details.${flag.key}`
                      )}
                    </strong>
                    : {flag.value}
                  </div>
                ))}
              </span>
            </div>
          )}
        </ViewTable>
      </div>
    </React.Fragment>
  );
};
